import { useEffect, useRef } from 'react';
import { RecoilValue, useRecoilCallback, useResetRecoilState } from 'recoil';
import { useRemoveAnnotation } from '~/hooks/useRemoveAnnotation';
import { annotationInteraction } from '~/hooks/viewer/annotationOverlay';
import {
    hoveredAnnotationState,
    selectedAnnotationState,
} from '~/state/atoms/annotationListAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import {
    isRegionMode,
    MEASURE_MODES,
    MeasureMode,
} from '~/utils/annotationKinds';
import { isDialogOpen, shortcutRefusal } from '~/utils/textEntry';

type MeasureShortcut = 'deselect' | 'delete';

const SHORTCUT_OF_KEY = new Map<string, MeasureShortcut>([
    ['escape', 'deselect'],
    ['delete', 'delete'],
    // The key macOS labels "delete".
    ['backspace', 'delete'],
]);

/** Wheel travel per mode step: half a mouse notch, so a notch is one step. */
const WHEEL_STEP_PX = 50;

/** Plain keys only: Shift+Delete, for one, clears a slice. */
const measureShortcutFor = (event: KeyboardEvent) =>
    event.ctrlKey || event.metaKey || event.altKey || event.shiftKey
        ? null
        : (SHORTCUT_OF_KEY.get(event.key.toLowerCase()) ?? null);

/**
 * Binds the Measure tool's keys on placed shapes: Esc drops the selection and
 * Delete or Backspace delete the shape under the pointer. While measuring,
 * Shift+wheel over a tile steps to the next or previous mode, passing refused
 * regions, in place of the slice scroll. Mount one instance only.
 *
 * Esc is read in the window's capture phase and claimed only under the tools
 * that act on the selection, so a panel or a maximized tile closes on the next
 * one. Delete and Backspace are read in the document's capture phase, ahead of
 * the engine and the structure Delete handlers, which ignore
 * `defaultPrevented`: a Delete that deleted a shape is stopped, repeats
 * included.
 */
export function useMeasureShortcuts(handlers: {
    /** The mode armed now; null while another tool is active. */
    active: MeasureMode | null;
    /** Why the regions are refused, localized; undefined when they are offered. */
    regionRefusal: string | undefined;
    select: (mode: MeasureMode) => void;
}): void {
    const latest = useRef(handlers);
    useEffect(() => {
        latest.current = handlers;
    });
    // Read at the key press, so the hook subscribes to no state.
    const read = useRecoilCallback(
        ({ snapshot }) =>
            <T>(state: RecoilValue<T>): T =>
                snapshot.getLoadable(state).getValue(),
        []
    );
    const deselect = useResetRecoilState(selectedAnnotationState);
    const removeAnnotation = useRemoveAnnotation();

    useEffect(() => {
        // Whether the Delete or Backspace held down deleted a shape.
        let deleteHeld = false;
        // Shift+wheel travel not yet stepped, signed by its direction.
        let travel = 0;
        // A re-arm would drop the shape or stroke being drawn.
        const busy = () =>
            read(
                viewManagerState
            )?.interactionContext.toolManager.isActiveToolActionInProgress() ??
            false;
        const refused = (event: KeyboardEvent) => {
            const focus = document.activeElement;
            return (
                event.defaultPrevented ||
                // An open menu or drop-down list takes letters and Esc.
                !!focus?.closest('[role="menu"], [role="listbox"]') ||
                shortcutRefusal(event, focus, isDialogOpen(), busy()) !== null
            );
        };

        const onKeyDown = (event: KeyboardEvent): void => {
            deleteHeld &&= event.repeat;
            if (measureShortcutFor(event) !== 'delete') {
                return;
            }
            const id = deleteHeld ? null : read(hoveredAnnotationState);
            if (id !== null && !refused(event)) {
                removeAnnotation(id);
                deleteHeld = true;
            }
            if (deleteHeld) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        const onEscape = (event: KeyboardEvent): void => {
            if (
                measureShortcutFor(event) === 'deselect' &&
                !refused(event) &&
                read(selectedAnnotationState) !== null
            ) {
                deselect();
                const tool = read(selectedToolState);
                if (annotationInteraction(tool, false) !== 'none') {
                    event.preventDefault();
                }
            }
        };

        const onWheel = (event: WheelEvent): void => {
            const { active, regionRefusal, select } = latest.current;
            if (
                !event.shiftKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.altKey ||
                active === null ||
                !(event.target as Element).closest?.(
                    '[data-viewport-container]'
                ) ||
                isDialogOpen() ||
                busy()
            ) {
                return;
            }
            // Kept from the engine, which would scroll the slice.
            event.preventDefault();
            event.stopPropagation();
            // Browsers move a Shift+wheel onto the horizontal axis.
            const delta = event.deltaY || event.deltaX;
            travel =
                Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;
            if (Math.abs(travel) < WHEEL_STEP_PX) {
                return;
            }
            // The tray's order, stepped round the ends.
            const modes = MEASURE_MODES.filter(
                (mode) => !isRegionMode(mode) || regionRefusal === undefined
            );
            const next = modes.indexOf(active) + Math.sign(travel);
            travel = 0;
            select(modes.at(next % modes.length) ?? active);
        };

        document.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('keydown', onEscape, true);
        // Not passive: a window wheel listener is passive unless told, and
        // could then not keep the browser from scrolling sideways.
        const wheel = { capture: true, passive: false };
        window.addEventListener('wheel', onWheel, wheel);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('keydown', onEscape, true);
            window.removeEventListener('wheel', onWheel, wheel);
        };
    }, [read, deselect, removeAnnotation]);
}
